// src/app/shared/components/form/form-wizard/form-actions-bar.component.ts
// Reusable Form Actions Bar - Sticky top bar with Save/Cancel buttons
// Provides consistent UX for all forms in the application

import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
} from '@angular/core';


// PrimeNG 20 Modules
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { BadgeModule } from 'primeng/badge';

@Component({
  selector: 'app-form-actions-bar',
  standalone: true,
  imports: [
    ButtonModule,
    TooltipModule,
    BadgeModule
],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="form-actions-bar">
      <div class="form-actions-bar__container">

        <!-- Left Side: Status Indicators -->
        <div class="form-actions-bar__status">
          <!-- Unsaved Changes Badge -->
          @if (isDirty && !isSubmitting) {
            <div
              class="form-actions-bar__badge"
              pTooltip="Masz niezapisane zmiany"
              tooltipPosition="bottom"
              >
              <i class="pi pi-exclamation-circle"></i>
              <span>Niezapisane zmiany</span>
            </div>
          }

          <!-- Validation Error Badge -->
          @if (!isValid && isDirty && !isSubmitting) {
            <div
              class="form-actions-bar__badge form-actions-bar__badge--error"
              pTooltip="Formularz zawiera błędy"
              tooltipPosition="bottom"
              >
              <i class="pi pi-times-circle"></i>
              <span class="hidden md:inline">Błędy walidacji</span>
            </div>
          }

          <!-- Saving Indicator -->
          @if (isSubmitting) {
            <div
              class="form-actions-bar__badge form-actions-bar__badge--saving"
              >
              <i class="pi pi-spin pi-spinner"></i>
              <span>Zapisywanie...</span>
            </div>
          }
        </div>

        <!-- Right Side: Action Buttons -->
        <div class="form-actions-bar__actions">
          <!-- Cancel Button -->
          <p-button
            [label]="cancelButtonText || 'Anuluj'"
            [icon]="cancelButtonIcon || 'pi pi-times'"
            severity="secondary"
            [outlined]="true"
            [disabled]="isSubmitting"
            (onClick)="cancel.emit()"
            pTooltip="Anuluj i wróć (Esc)"
            tooltipPosition="bottom"
            />

          <!-- Save Button -->
          <p-button
            [label]="saveButtonText"
            [icon]="saveButtonIcon"
            [disabled]="!isValid || isSubmitting"
            [loading]="isSubmitting"
            (onClick)="save.emit()"
            [pTooltip]="getSaveTooltip()"
            tooltipPosition="bottom"
            />
        </div>
      </div>
    </div>
    `,
  styles: [`
    .form-actions-bar {
      position: sticky;
      bottom: 0;
      z-index: 1000;
      background: var(--p-surface-50);
      border-top: 1px solid var(--p-surface-300);
      box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.05);
    }

    .form-actions-bar__container {
      display: flex;
      align-items: center;
      justify-content: space-between;
      max-width: 1200px;
      margin: 0 auto;
      padding: 0.75rem 1.5rem;
      gap: 1rem;
    }

    .form-actions-bar__status {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      flex: 1;
      min-width: 0;
    }

    .form-actions-bar__badge {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem 0.75rem;
      background: var(--p-orange-50);
      color: var(--p-orange-700);
      border: 1px solid var(--p-orange-200);
      border-radius: 6px;
      font-size: 0.875rem;
      font-weight: 600;
      white-space: nowrap;

      i {
        font-size: 1rem;
      }

      &--error {
        background: var(--p-red-50);
        color: var(--p-red-700);
        border-color: var(--p-red-200);
      }

      &--saving {
        background: var(--p-blue-50);
        color: var(--p-blue-700);
        border-color: var(--p-blue-200);
      }
    }

    .form-actions-bar__actions {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }

    // ========================================
    // RESPONSIVE
    // ========================================
    @media (max-width: 768px) {
      .form-actions-bar__container {
        padding: 0.625rem 1rem;
      }

      .form-actions-bar__badge {
        padding: 0.375rem 0.625rem;
        font-size: 0.8125rem;

        span {
          display: none;
        }

        i {
          font-size: 0.9rem;
        }
      }

      .form-actions-bar__actions {
        gap: 0.5rem;

        ::ng-deep .p-button {
          padding: 0.5rem 0.75rem;
          font-size: 0.875rem;

          .p-button-label {
            display: none;
          }
        }
      }
    }

    @media (max-width: 480px) {
      .form-actions-bar__status {
        display: none;
      }
    }
  `],
})
export class FormActionsBarComponent {
  // ============================================
  // INPUTS
  // ============================================

  /** Is form valid */
  @Input() isValid = false;

  /** Has form been modified */
  @Input() isDirty = false;

  /** Is form currently submitting */
  @Input() isSubmitting = false;

  /** Is this edit mode (vs create) */
  @Input() isEditMode = false;

  /** Custom save button text */
  @Input() submitButtonText?: string;

  /** Custom save button icon */
  @Input() submitButtonIcon?: string;

  /** Custom cancel button text */
  @Input() cancelButtonText?: string;

  /** Custom cancel button icon */
  @Input() cancelButtonIcon?: string;

  // ============================================
  // OUTPUTS
  // ============================================

  /** Emitted when save button is clicked */
  @Output() save = new EventEmitter<void>();

  /** Emitted when cancel button is clicked */
  @Output() cancel = new EventEmitter<void>();

  // ============================================
  // COMPUTED PROPERTIES
  // ============================================

  /**
   * Get save button text based on mode
   */
  get saveButtonText(): string {
    if (this.submitButtonText) {
      return this.submitButtonText;
    }

    return this.isEditMode ? 'Zapisz zmiany' : 'Utwórz';
  }

  /**
   * Get save button icon based on mode
   */
  get saveButtonIcon(): string {
    if (this.submitButtonIcon) {
      return this.submitButtonIcon;
    }

    return this.isEditMode ? 'pi pi-check' : 'pi pi-plus';
  }

  /**
   * Get save button tooltip
   */
  getSaveTooltip(): string {
    if (!this.isValid && this.isDirty) {
      return 'Wypełnij wszystkie wymagane pola';
    }

    if (this.isSubmitting) {
      return 'Zapisywanie...';
    }

    if (this.isEditMode) {
      return 'Zapisz zmiany (Ctrl + Enter)';
    }

    return 'Utwórz nowy rekord (Ctrl + Enter)';
  }
}
